/**
 * Copyright (c) 2025-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { gql, type TypedDocumentNode } from "@apollo/client";
import { skipToken, useBackgroundQuery, useReadQuery, type QueryRef } from "@apollo/client/react";
import { usePopoverContext } from "@ark-ui/react/popover";
import {
  ArrowRightLine,
  ArrowRightShortLine,
  CloseLine,
  ExternalLinkLine,
  ForumOutlined,
  HeartFill,
  HeartLine,
  LogoutBoxRightLine,
  MenuLine,
  MovieLine,
  RobotFill,
  UserLine,
} from "@ndla/icons";
import { Button, Heading, PopoverRoot, PopoverTrigger, Text } from "@ndla/primitives";
import { SafeLink, SafeLinkButton, type SafeLinkButtonProps, type SafeLinkProps } from "@ndla/safelink";
import { styled } from "@ndla/styled-system/jsx";
import type { ParseKeys } from "i18next";
import { Suspense, useContext, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../components/AuthenticationContext";
import { LanguageSelector } from "../../components/LanguageSelector/LanguageSelector";
import config from "../../config";
import { FILM_PAGE_URL, MULTIDISCIPLINARY_URL, TOOLBOX_STUDENT_URL, TOOLBOX_TEACHER_URL } from "../../constants";
import type {
  GQLDynamicMenuQuery,
  GQLDynamicMenuQueryVariables,
  GQLMastheadFavoriteSubjectsQuery,
  GQLMastheadFavoriteSubjectsQueryVariables,
} from "../../graphqlTypes";
import { routes } from "../../routeHelpers";
import { useRawLocation } from "../../util/localePath";
import { toHref } from "../../util/urlHelper";
import { MastheadPopoverBackdrop, MastheadPopoverContent } from "./MastheadPopover";

const NavLink = (props: SafeLinkProps) => {
  const { setOpen } = usePopoverContext();
  return (
    <SafeLink
      {...props}
      onClick={(e) => {
        setOpen(false);
        props.onClick?.(e);
      }}
    />
  );
};

const NavSafeLinkButton = (props: SafeLinkButtonProps) => {
  const { setOpen } = usePopoverContext();
  return (
    <SafeLinkButton
      {...props}
      onClick={(e) => {
        setOpen(false);
        props.onClick?.(e);
      }}
    />
  );
};

const StyledSafeLink = styled(NavLink, {
  base: {
    display: "flex",
    gap: "3xsmall",
    alignItems: "center",
    color: "text.default",
    textStyle: "body.link",
    textDecoration: "underline",
    _hover: {
      textDecoration: "none",
    },
  },
});

const DrawerButton = styled(Button, {
  base: {
    tabletDown: {
      paddingInline: "xsmall",
      "& span": {
        display: "none",
      },
    },
  },
});

const StyledList = styled("ul", {
  base: {
    display: "flex",
    flexDirection: "column",
    gap: "3xsmall",
  },
});

const ListsWrapper = styled("div", {
  base: {
    display: "flex",
    flexWrap: "wrap",
    gap: "xlarge",
  },
});

const NavigationListWrapper = styled("div", {
  base: {
    display: "flex",
    flexDirection: "column",
    gap: "3xsmall",
  },
});

const dynamicMenuQueryDef: TypedDocumentNode<GQLDynamicMenuQuery, GQLDynamicMenuQueryVariables> = gql`
  query dynamicMenu {
    frontpage {
      # If we don't include articleId, the query response will be overridden
      articleId
      menu {
        articleId
        article {
          id
          title
          slug
          revision
        }
      }
    }
  }
`;

type DynamicMenuQueryRef = QueryRef<
  GQLDynamicMenuQuery,
  GQLDynamicMenuQueryVariables,
  "complete" | "streaming" | "empty"
>;

const favoriteSubjectsQueryDefinition: TypedDocumentNode<
  GQLMastheadFavoriteSubjectsQuery,
  GQLMastheadFavoriteSubjectsQueryVariables
> = gql`
  query mastheadFavoriteSubjects($ids: [String!]!) {
    nodes(nodeType: "SUBJECT", ids: $ids) {
      id
      name
      url
    }
  }
`;

type FavoriteSubjectsQueryRef = QueryRef<
  GQLMastheadFavoriteSubjectsQuery,
  GQLMastheadFavoriteSubjectsQueryVariables,
  "complete" | "streaming" | "empty"
>;

export const MastheadMenu = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { pathname } = useRawLocation();
  const [prevPathname, setPrevPathname] = useState(pathname);
  const { user, authenticated } = useContext(AuthContext);

  const [dynamicMenuQueryRef] = useBackgroundQuery(dynamicMenuQueryDef, typeof window === "undefined" ? skipToken : {});

  const [favouriteSubjectsQueryRef] = useBackgroundQuery(
    favoriteSubjectsQueryDefinition,
    !authenticated || !user?.favoriteSubjects.length
      ? skipToken
      : { variables: { ids: user.favoriteSubjects.toReversed().slice(0, 5) } },
  );

  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    if (open) setOpen(false);
  }

  return (
    <PopoverRoot open={open} onOpenChange={(details) => setOpen(details.open)}>
      <PopoverTrigger asChild>
        <DrawerButton
          variant="tertiary"
          data-testid="masthead-menu-button"
          aria-label={t(`masthead.menu.${open ? "close" : "open"}`)}
          title={t(`masthead.menu.${open ? "close" : "open"}`)}
        >
          {open ? <CloseLine /> : <MenuLine />}
          <span>{t("masthead.menu.button")}</span>
        </DrawerButton>
      </PopoverTrigger>
      <MastheadPopoverContent>
        <NavigationPart
          dynamicMenuQueryRef={dynamicMenuQueryRef}
          favoriteSubjectsQueryRef={favouriteSubjectsQueryRef}
        />
        <MyNdlaPart />
      </MastheadPopoverContent>
      <MastheadPopoverBackdrop present={open} />
    </PopoverRoot>
  );
};

const NavigationPartWrapper = styled("div", {
  base: {
    display: "flex",
    paddingInlineStart: "xxlarge",
    flexDirection: "column",
    gap: "medium",
    paddingBlockStart: "medium",
    paddingBlockEnd: "large",
    paddingInline: "xxlarge",
  },
});

const educationLinks: StaticLinkType[] = [
  { to: "/", text: "masthead.menu.links.education.programmes" },
  { to: "/subjects", text: "masthead.menu.links.education.subjects" },
  { to: MULTIDISCIPLINARY_URL, text: "masthead.menu.links.education.multidisciplinary" },
];

const tipLinks: StaticLinkType[] = [
  { to: TOOLBOX_STUDENT_URL, text: "masthead.menu.links.tips.studentToolbox" },
  { to: TOOLBOX_TEACHER_URL, text: "masthead.menu.links.tips.teacherToolbox" },
];

const FavoriteSubjectsList = styled("ul", {
  base: {
    display: "flex",
    flexWrap: "wrap",
    gap: "medium",
  },
});

const StyledLanguageSelector = styled(LanguageSelector, {
  base: {
    alignSelf: "flex-start",
    desktop: {
      display: "none",
    },
  },
});

const NavigationPartLink = styled(NavLink, {
  base: {
    marginBlockStart: "small",
    color: "text.link",
    textDecoration: "underline",
    _hover: {
      textDecoration: "none",
    },
    _visited: {
      color: "text.linkVisited",
    },
  },
});

interface NavigationPartProps {
  dynamicMenuQueryRef?: DynamicMenuQueryRef;
  favoriteSubjectsQueryRef?: FavoriteSubjectsQueryRef;
}

const NavigationPart = ({ dynamicMenuQueryRef, favoriteSubjectsQueryRef }: NavigationPartProps) => {
  const { t } = useTranslation();

  return (
    <NavigationPartWrapper>
      <Heading asChild consumeCss textStyle="heading.small">
        <h2>{t("masthead.menu.title")}</h2>
      </Heading>
      <ListsWrapper>
        <NavigationList
          title={t("masthead.menu.links.education.title")}
          items={educationLinks.map((link) => ({ to: link.to, text: t(link.text) }))}
        />
        <NavigationList
          title={t("masthead.menu.links.tips.title")}
          items={tipLinks.map((link) => ({ to: link.to, text: t(link.text) }))}
        />
        {!!dynamicMenuQueryRef && (
          <Suspense>
            <DynamicLinksList dynamicMenuQueryRef={dynamicMenuQueryRef} />
          </Suspense>
        )}
      </ListsWrapper>
      {!!favoriteSubjectsQueryRef && (
        <Suspense>
          <FavoriteSubjectsPart favoriteSubjectsQueryRef={favoriteSubjectsQueryRef} />
        </Suspense>
      )}
      <StyledLanguageSelector variant="secondary" />
    </NavigationPartWrapper>
  );
};

type LinkType = { text: string; to: string };
type StaticLinkType = { text: ParseKeys; to: string };

interface NavigationListProps {
  title: string;
  items: LinkType[];
}

const NavigationList = ({ title, items }: NavigationListProps) => {
  const id = useId();

  return (
    <NavigationListWrapper>
      <Heading asChild consumeCss textStyle="title.small">
        <h3 id={id}>{title}</h3>
      </Heading>
      <StyledList aria-labelledby={id}>
        {items.map((item) => (
          <li key={item.to}>
            <StyledSafeLink to={item.to}>
              <ArrowRightShortLine size="small" />
              {item.text}
            </StyledSafeLink>
          </li>
        ))}
      </StyledList>
    </NavigationListWrapper>
  );
};

interface DynamicLinksListProps {
  dynamicMenuQueryRef: DynamicMenuQueryRef;
}

const DynamicLinksList = ({ dynamicMenuQueryRef }: DynamicLinksListProps) => {
  const { t } = useTranslation();
  const dynamicMenuQuery = useReadQuery(dynamicMenuQueryRef);
  const menu = dynamicMenuQuery.data?.frontpage?.menu;
  const dynamicLinks = useMemo(() => {
    if (!menu?.length) return [];
    return menu.map((item) => ({
      text: item.article.title,
      to: `/om/${item.article.slug}`,
    }));
  }, [menu]);

  if (!dynamicLinks.length) return null;

  return <NavigationList title={t("masthead.menu.links.dynamic.title")} items={dynamicLinks} />;
};

interface FavoriteSubjectsPartProps {
  favoriteSubjectsQueryRef: FavoriteSubjectsQueryRef;
}

const FavoriteSubjectsPart = ({ favoriteSubjectsQueryRef }: FavoriteSubjectsPartProps) => {
  const { t } = useTranslation();
  const favouriteSubjectsQuery = useReadQuery(favoriteSubjectsQueryRef);
  const favouriteSubjects = favouriteSubjectsQuery.data?.nodes;

  if (!favouriteSubjects?.length) return null;

  return (
    <NavigationListWrapper>
      <Heading asChild consumeCss textStyle="label.large" fontWeight="bold">
        <h2>{t("masthead.menu.myNdla.yourFavouriteSubjects")}</h2>
      </Heading>
      <FavoriteSubjectsList>
        {favouriteSubjects.map((subject) => (
          <li key={subject.id}>
            <StyledSafeLink to={subject.url ?? ""}>
              <HeartFill size="small" />
              {subject.name}
            </StyledSafeLink>
          </li>
        ))}
      </FavoriteSubjectsList>
      <NavigationPartLink to={routes.myNdla.subjects}>
        {t("masthead.menu.myNdla.viewAllFavouriteSubjects")}
        <ArrowRightLine />
      </NavigationPartLink>
    </NavigationListWrapper>
  );
};

const MyNdlaWrapper = styled("div", {
  base: {
    display: "flex",
    flexDirection: "column",
    paddingInlineStart: "large",
    paddingInlineEnd: "xxlarge",
    paddingBlock: "large",
    gap: "large",
    background: "background.subtle",
  },
});

const StyledText = styled(Text, {
  base: {
    display: "flex",
    flexDirection: "column",
    gap: "3xsmall",
  },
});

const ButtonsContainer = styled("div", {
  base: {
    display: "flex",
    flexDirection: "column",
    gap: "xsmall",
  },
});

const MyNdlaSafeLinkButton = styled(NavSafeLinkButton, {
  base: {
    justifyContent: "flex-start",
    textWrap: "nowrap",
  },
});

const LogoutSafeLinkButton = styled(StyledSafeLink, {
  base: {
    marginBlockStart: "auto",
    alignSelf: "flex-start",
  },
});

const MyNdlaPart = () => {
  const { user, authenticated } = useContext(AuthContext);
  const { t } = useTranslation();
  const location = useRawLocation();

  return (
    <MyNdlaWrapper>
      {!!authenticated && !!user && (
        <StyledText>
          <Text textStyle="body.medium" asChild consumeCss>
            <span>{t("masthead.menu.myNdla.loggedInAs")}</span>
          </Text>
          <Text textStyle="title.large" asChild consumeCss>
            <span>{user.displayName}</span>
          </Text>
        </StyledText>
      )}
      <ButtonsContainer>
        {authenticated && user ? (
          <MyNdlaSafeLinkButton to={routes.myNdla.root} variant="secondary">
            <HeartLine />
            {t("masthead.menu.myNdla.myNdla")}
          </MyNdlaSafeLinkButton>
        ) : (
          <MyNdlaSafeLinkButton variant="secondary" to={`/login?returnTo=${routes.myNdla.root}`} reloadDocument>
            <UserLine />
            {t("masthead.menu.myNdla.myNdla")}
          </MyNdlaSafeLinkButton>
        )}

        <MyNdlaSafeLinkButton
          to={`https://${config.arenaDomain}`}
          target="_blank"
          rel="noopener noreferrer"
          variant="secondary"
        >
          <ForumOutlined />
          {t("welcomePage.quickLinks.arena.title")}
          <ExternalLinkLine />
        </MyNdlaSafeLinkButton>
        <MyNdlaSafeLinkButton to={config.ndlaKiDomain} target="_blank" rel="noopener noreferrer" variant="secondary">
          <RobotFill />
          {t("welcomePage.quickLinks.chatRobot.title")}
          <ExternalLinkLine />
        </MyNdlaSafeLinkButton>
        <MyNdlaSafeLinkButton to={FILM_PAGE_URL} variant="secondary">
          <MovieLine />
          {t("welcomePage.quickLinks.film.title")}
        </MyNdlaSafeLinkButton>
      </ButtonsContainer>
      {!!authenticated && !!user && (
        <LogoutSafeLinkButton to={`/logout?returnTo=${toHref(location)}`} reloadDocument>
          <LogoutBoxRightLine />
          {t("user.buttonLogOut")}
        </LogoutSafeLinkButton>
      )}
    </MyNdlaWrapper>
  );
};
